import { useAuth } from "@healthos/auth/react";
import { useMemberName } from "../../hooks/use-member-name";
import { useDashboard, useOperationsMe } from "../../pages/operations/dashboard/_hooks";
import { formatNumber, hasCap } from "../../pages/operations/dashboard/_shared";
import { useOpsCompanies, useOpsCompany } from "./ops-company-context";

/** Human labels for the ops role matrix (mirrors lib/ops-rbac.ts), widest
 *  tier first so a caller holding several roles wears the strongest pill. */
const ROLE_LABELS: Array<[role: string, label: string]> = [
  ["admin", "Platform"],
  ["admin:healthos", "Platform"],
  ["ops_admin", "Ops"],
  ["admin:healthos:ops", "Ops"],
  ["ops_csm", "CSM"],
  ["admin:healthos:csm", "CSM"],
  ["ops_analyst", "Analyst"],
  ["user:healthos:ops-analyst", "Analyst"],
  ["user:healthos:customer-admin", "Employer admin"],
  ["ops_guest", "Guest"],
  ["user:healthos:ops-guest", "Guest"],
];

function roleLabelFor(roles: string[]): string {
  for (const [role, label] of ROLE_LABELS) {
    if (roles.includes(role)) return label;
  }
  return roles[0]?.replace("ops_", "") ?? "guest";
}

/**
 * Persistent honesty strip. Two layers, both always true: the live dot
 * carries what is measured (enrolled count), the MODELED FROM BENCHMARKS
 * label carries what is not. Platform callers additionally get the
 * company switcher, which scopes every ops query below it.
 */
export function OpsChromeBar() {
  const dashboardQuery = useDashboard();
  const meQuery = useOperationsMe();
  const { state: authState } = useAuth();
  const data = dashboardQuery.data;
  const me = meQuery.data ?? null;

  const roleLabel = roleLabelFor(me?.roles ?? []);
  const profile = authState.status === "signedIn" ? authState.user.profile : null;
  const memberName = useMemberName();
  const personName = memberName?.fullName ?? profile?.email ?? null;
  const updated = data
    ? new Date(data.generatedAt).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <header className="border-border-default border-b bg-surface-elevated/60 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[1480px] flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3">
        {data ? (
          data.mode === "prospect" ? (
            <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-tertiary">
              Sample view · illustrative benchmarks
            </span>
          ) : (
            <>
              <span
                className="font-mono text-xs uppercase tracking-[0.16em] text-ink-tertiary"
                title="Dollar figures are modeled from published benchmarks until your claims feed is connected"
              >
                Modeled from benchmarks
              </span>
              <span aria-hidden className="text-mist-dark">
                ·
              </span>
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-teal-800">
                <span
                  aria-hidden
                  className="inline-block h-[7px] w-[7px] rounded-full bg-leaf-dark shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-leaf-dark)_18%,transparent)]"
                />
                {formatNumber(data.population.enrolledCount)} enrolled live
              </span>
            </>
          )
        ) : (
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-tertiary">
            Loading…
          </span>
        )}

        {hasCap("ops:all_companies", me) ? <CompanySwitcher /> : null}

        <div className="ml-auto flex items-center gap-3">
          {updated ? (
            <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-quaternary">
              Updated {updated}
            </span>
          ) : null}
          <span className="rounded-full border border-border-default bg-surface-elevated/70 px-3 py-0.5 font-mono text-xs uppercase tracking-[0.16em] text-ink-body">
            {roleLabel}
            {personName ? (
              <>
                <span aria-hidden className="mx-1.5 text-mist-dark">
                  ·
                </span>
                {personName}
              </>
            ) : null}
          </span>
        </div>
      </div>
    </header>
  );
}

/** Company scope for platform callers, labelled by a live Identity GraphQL read. */
function CompanySwitcher() {
  const { companyId, setCompanyId } = useOpsCompany();
  const companiesQuery = useOpsCompanies(true);
  const companies = companiesQuery.data?.companies ?? [];
  if (companies.length === 0) return null;
  return (
    <select
      aria-label="Company"
      value={companyId ?? ""}
      onChange={(e) => setCompanyId(e.target.value || null)}
      // min-h-6 keeps the pointer target at the 24px WCAG 2.5.8 floor.
      className="min-h-6 cursor-pointer rounded-full border border-border-default bg-surface-elevated px-3 py-0.5 font-mono text-xs font-semibold uppercase tracking-[0.06em] text-ink-primary transition-colors duration-150 hover:border-teal-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/30"
    >
      <option value="">All companies</option>
      {companies.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name ?? "Company not named in Identity"} · {formatNumber(c.members)}{" "}
          {c.members === 1 ? "member" : "members"}
        </option>
      ))}
    </select>
  );
}
